import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {ButtonGroup, Dropdown, Header} from 'semantic-ui-react';

import {withApollo} from 'react-apollo';
import {Authorization} from '@appComponents/Authorization';
import ActionButton from '@appComponents/ButtonsCollection/ActionButton';
import {getLink} from '@appComponents/Link';
import {ARGOS_JOB_LIVE_CONTROL} from '@constants/resources';
import {
    ARGOS_DASHBOARD_URL,
    ARGOS_STOPPABLE_JOB_STATUSES,
    EVENT_STATUS_PLANNED,
    SECURITY_PRIVILEGES_UPDATE,
} from '@constants/variables';
import {showMessageBox} from '@utils/messageBox';
import {hideModal, showModalConfirmation} from '@utils/modal';
import mapModulesToProps from '@utils/mapModulesToProps';
import {StartArgosJob, StopArgosJob, ReenqueueArgosJob} from '@graphql/argosJob/mutation';
import {canShowReenqueueArgosJob} from '@modules/events/utils/argosJobActionRules';

import {openArgosJobConfigureModal} from '../utils/openArgosJobConfigureModal';

const ARGOS_DASHBOARD_TEMPLATE_URL = `${ARGOS_DASHBOARD_URL}?`
    + 'filters=%7B"showRunning"%3Atrue%2C"showTerminated"%3Atrue%2C"order"%3A%5B%5B1%2C"asc"%5D%5D'
    + '%2C"jobUuid"%3A"<jobUuid>"%7D';

const argosJobUpdateAuthorization = {
    [ARGOS_JOB_LIVE_CONTROL]: SECURITY_PRIVILEGES_UPDATE,
};

const canStartArgosJob = (row) => {
    if (row.event_status_id !== EVENT_STATUS_PLANNED || row.job_uuid) {
        return false;
    }

    if (!row.start_datetime) {
        return true;
    }

    return new Date(row.start_datetime) > new Date();
};

const canStopArgosJob = (row) => {
    const jobStatus = String(row.job_status ?? '').toLowerCase();

    if (!row.job_uuid || !ARGOS_STOPPABLE_JOB_STATUSES.includes(jobStatus)) {
        return false;
    }

    if (!row.end_datetime) {
        return true;
    }

    return new Date(row.end_datetime) > new Date();
};

const ArgosJobLiveControlActions = (props) => {
    const {row, refetch} = props;
    const history = useHistory();

    const runMutation = (mutation, successMessage) => {
        return props.client.mutate({
            mutation,
            variables: {id: row.id},
        }).then(() => {
            showMessageBox('argosJobLiveControl', successMessage, null, 'success');

            if (refetch) {
                refetch();
            }
        }).catch((error) => {
            showMessageBox('argosJobLiveControl', `Error: ${error.message}`, null, 'error');
        });
    };

    const createConfirmationParams = (title, text, mutation, successMessage) => ({
        header: <Header content={title}/>,
        text,
        onYes: () => {
            hideModal();

            return runMutation(mutation, successMessage);
        },
    });

    const renderStartArgosJobButton = () => (
        canStartArgosJob(row)
            ? (
                <ActionButton
                    popup={{content: 'Start Argos job'}}
                    button={{
                        icon: 'play',
                        color: 'green',
                    }}
                    actionParams={createConfirmationParams(
                        'Start Argos job',
                        'Are you sure you want to start this Argos job?',
                        StartArgosJob,
                        'Argos job start has been requested.'
                    )}
                    onClick={showModalConfirmation}
                />
            )
            : null
    );

    const renderStopArgosJobButton = () => (
        canStopArgosJob(row)
            ? (
                <ActionButton
                    popup={{content: 'Stop Argos job'}}
                    button={{
                        icon: 'stop',
                        color: 'orange',
                    }}
                    actionParams={createConfirmationParams(
                        'Stop Argos job',
                        'Are you sure you want to stop this Argos job?',
                        StopArgosJob,
                        'Argos job stop has been requested.'
                    )}
                    onClick={showModalConfirmation}
                />
            )
            : null
    );

    const onConfigure = () => {
        openArgosJobConfigureModal({
            row,
            onSaveSuccess: refetch,
        });
    };

    const onShowInDashboard = () => {
        const url = ARGOS_DASHBOARD_TEMPLATE_URL.replace('<jobUuid>', row.job_uuid || '');

        window.open(url, '_blank', 'noopener');
    };

    return (
        <Authorization authorization={argosJobUpdateAuthorization}>
            <div className='liveControlActionButtons'>
                <ButtonGroup size='mini' className='actionButton' style={{display: 'block', whiteSpace: 'nowrap'}}>
                    {renderStartArgosJobButton()}
                    {renderStopArgosJobButton()}
                </ButtonGroup>
                <ButtonGroup size='mini' className='actionButton' style={{display: 'block', whiteSpace: 'nowrap'}}>
                    <Dropdown
                        button
                        className='icon --defaultPadding'
                        icon='ellipsis horizontal'
                        simple
                    >
                        <Dropdown.Menu>
                            <Dropdown.Item
                                icon='cog'
                                text='Configure the Argos job'
                                onClick={onConfigure}
                            />
                            {row.job_uuid && (
                                <Dropdown.Item
                                    icon='external'
                                    text='Show the Argos job in the Argos dashboard'
                                    onClick={onShowInDashboard}
                                />
                            )}
                            {row.job_uuid && (
                                <Dropdown.Item
                                    icon='file'
                                    text='Job history'
                                    onClick={() => history.push(getLink(
                                        'argosJobLiveControl.jobHistory',
                                        {jobId: row.id}
                                    ))}
                                />
                            )}
                            {row.job_uuid && (
                                <Dropdown.Item
                                    icon='file'
                                    text='Stream history'
                                    onClick={() => history.push(getLink(
                                        'argosJobLiveControl.streamHistory',
                                        {jobId: row.id}
                                    ))}
                                />
                            )}
                            {canShowReenqueueArgosJob(row) && (
                                <Dropdown.Item
                                    icon='redo'
                                    text='Re-enqueue stream'
                                    onClick={() => showModalConfirmation(createConfirmationParams(
                                        'Re-enqueue stream',
                                        'Are you sure you want to re-enqueue streams for this Argos job?',
                                        ReenqueueArgosJob,
                                        'Argos job stream re-enqueue has been requested.'
                                    ))}
                                />
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </ButtonGroup>
            </div>
        </Authorization>
    );
};

ArgosJobLiveControlActions.propTypes = {
    row: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        event_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        event_type_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        event_start_datetime: PropTypes.string,
        event_end_datetime: PropTypes.string,
        event_status_id: PropTypes.number,
        job_uuid: PropTypes.string,
        job_status: PropTypes.string,
        stream_status: PropTypes.string,
        start_datetime: PropTypes.string,
        end_datetime: PropTypes.string,
    }).isRequired,
    refetch: PropTypes.func,
    client: PropTypes.shape({
        mutate: PropTypes.func.isRequired,
        query: PropTypes.func,
    }).isRequired,
};

const mapDispatchToProps = mapModulesToProps(['Modal']);

export default withApollo(connect(null, mapDispatchToProps)(ArgosJobLiveControlActions));
